import { ReactNode } from 'react';
import { Comparer } from '@/features/comparer';
import { LegalCopy } from '@/features/legal/legalCopy/LegalCopy';
import { UploaderLabel } from '@/features/uploadMedia/UploaderLabel';
import { HCaptchaDisclosure } from '@/ui/HCaptchaDisclosure';
import { useLocalization } from '@/services/localizationService';
import uploadWhiteIcon from '@/public/images/icons/upload-white.svg';
import Footer from '../Footer';
import { AnimatedDiv } from './animatedDiv/AnimatedDiv';
import styles from './leftCenteredHomeLayout.module.css';

type Props = {
  filePickerId: string;
  comparerConfig: {
    beforeSrc: string;
    afterSrc: string;
    initialSliderProgress: number;
    maxSliderProgress: number;
  };
  onComparerSlide: () => void;
  bipaDisclaimerEnabled: boolean;
  bipaText: ReactNode;
  /* `left_centered_flipped`: mirrored hero whose pink wall extends behind the
   * copy column (see Diagram, frame "Header — Opaque Pills"). */
  flipped?: boolean;
};

export function LeftCenteredHomeLayout({
  filePickerId,
  comparerConfig,
  onComparerSlide,
  bipaDisclaimerEnabled,
  bipaText,
  flipped = false,
}: Props) {
  const { t, data } = useLocalization();

  return (
    <div className={`${styles.layout} ${flipped ? styles.layoutFlipped : ''}`}>
      <div className={styles.content}>
        <AnimatedDiv>
          <p className={styles.eyebrow}>{t(data.home.leftCenteredEyebrow)}</p>
          {/* Diagram keeps "Remini does the rest." on a single line on
           * standard desktop sizes; narrow screens may wrap it. */}
          <h1 className={styles.headline}>
            <span className={styles.headlineLead}>
              {t(data.home.leftCenteredTitleLead)}
            </span>
            <span className={styles.headlineSupport}>
              {t(data.home.leftCenteredTitleSupport)}
            </span>
          </h1>
        </AnimatedDiv>
        <AnimatedDiv>
          <p className={styles.lead}>{t(data.home.leftCenteredSubtitle)}</p>
        </AnimatedDiv>
        <AnimatedDiv>
          <UploaderLabel forId={filePickerId} className={styles.cta}>
            {t(data.home.leftCenteredCta)}
            <img {...uploadWhiteIcon} className={styles.ctaIcon} alt="" />
          </UploaderLabel>
          <LegalCopy className={styles.legal} forceLegalLinks />
          {bipaDisclaimerEnabled && (
            <p className={`R12 contrast-low ${styles.bipa}`}>{bipaText}</p>
          )}
          <HCaptchaDisclosure
            className={styles.hcaptcha}
            textClassName="text-[10px] leading-[1.4] contrast-low"
          />
        </AnimatedDiv>
      </div>
      <div className={styles.proof}>
        {comparerConfig.beforeSrc && comparerConfig.afterSrc && (
          <Comparer
            className="w-full h-full"
            backgroundColor={0x0c0c0c}
            initialFit="cover"
            disablePan
            disablePinch
            {...comparerConfig}
            /* Diagram: the divider travels across the hero; the left copy
             * column must not clamp its range (unlike the control). */
            initialSliderProgress={0.5}
            minSliderProgress={0.06}
            maxSliderProgress={0.94}
            /* Pills flank the divider, the handle sits mid-height, and the
             * interaction is drag-only (no movement on plain hover). */
            badgesAlignment="center"
            onSlide={onComparerSlide}
            sliderBehavior="default"
          />
        )}
      </div>
      <Footer
        className="absolute bottom-0 start-0 z-10 w-content max-w-[45%] pointer-events-auto"
        forceLegalLinks
      />
    </div>
  );
}
